import { format } from 'date-fns';

import { formatTimeDisplay, isSameDate } from './dateTime';

export type MarketScheduleType = {
  date?: Date;
  openAt?: Date | string | null;
  closeAt?: Date | string | null;
  nextBusinessDay?: MarketScheduleType;
};

export const isMarketScheduleOpen = (marketSchedule: MarketScheduleType) => {
  return marketSchedule.openAt != null && marketSchedule.closeAt != null;
};

type SingleDayMarketScheduleHoursType = {
  currentDate: Date;
  marketSchedule: MarketScheduleType;
};

export const singleDayMarketScheduleHours = ({ currentDate, marketSchedule }: SingleDayMarketScheduleHoursType) => {
  const isMarketScheduleOpenValue = isMarketScheduleOpen(marketSchedule);

  const marketScheduleOpenTime = isMarketScheduleOpenValue ? new Date(marketSchedule.openAt!) : null;
  const marketScheduleCloseTime = isMarketScheduleOpenValue ? new Date(marketSchedule.closeAt!) : null;

  const isPreMarketHours = isMarketScheduleOpenValue && marketScheduleOpenTime && currentDate < marketScheduleOpenTime;
  const isDuringMarketHours =
    isMarketScheduleOpenValue &&
    marketScheduleOpenTime &&
    marketScheduleCloseTime &&
    currentDate >= marketScheduleOpenTime &&
    currentDate <= marketScheduleCloseTime;
  const isPostMarketHours =
    isMarketScheduleOpenValue && marketScheduleCloseTime && currentDate > marketScheduleCloseTime;

  const tomorrow = new Date(currentDate);
  tomorrow.setUTCDate(currentDate.getUTCDate() + 1);

  const { nextBusinessDay } = marketSchedule;

  let nextBusinessDayOpenTime = null;
  let isNextBusinessDayTomorrow = null;

  if (nextBusinessDay?.openAt != null) {
    nextBusinessDayOpenTime = new Date(nextBusinessDay.openAt);

    isNextBusinessDayTomorrow = isSameDate({
      date: tomorrow,
      nextDate: nextBusinessDayOpenTime,
    });
  }

  return {
    marketScheduleOpenTime,
    marketScheduleCloseTime,
    isPreMarketHours,
    isPostMarketHours,
    isDuringMarketHours,
    tomorrow,
    nextBusinessDayOpenTime,
    isNextBusinessDayTomorrow,
  };
};

type NextMarketOpenAtLabelForPreMarketType = {
  currentDate: Date;
  marketSchedule: MarketScheduleType;
};

export const nextMarketOpenAtLabelForPreMarket = ({
  currentDate,
  marketSchedule,
}: NextMarketOpenAtLabelForPreMarketType) => {
  const { marketScheduleOpenTime } = singleDayMarketScheduleHours({ currentDate, marketSchedule });

  const hasMarketScheduleOpenTime = marketScheduleOpenTime != null;
  const nextMarketOpenLabel = hasMarketScheduleOpenTime
    ? `Market opens at ${formatTimeDisplay(marketScheduleOpenTime)}`
    : null;
  const hasNextMarketOpenLabel = nextMarketOpenLabel != null;

  return {
    hasNextMarketOpenLabel,
    nextMarketOpenLabel,
  };
};

type NextMarketOpenAtLabelForClosedMarketType = {
  currentDate: Date;
  marketSchedule: MarketScheduleType;
};

export const nextMarketOpenAtLabelForClosedMarket = ({
  currentDate,
  marketSchedule,
}: NextMarketOpenAtLabelForClosedMarketType) => {
  const { nextBusinessDayOpenTime, isNextBusinessDayTomorrow } = singleDayMarketScheduleHours({
    currentDate,
    marketSchedule,
  });

  let nextMarketOpenLabel = null;

  if (isNextBusinessDayTomorrow !== null && nextBusinessDayOpenTime !== null) {
    nextMarketOpenLabel = isNextBusinessDayTomorrow
      ? `Market opens at ${formatTimeDisplay(nextBusinessDayOpenTime)}`
      : `Market opens ${format(nextBusinessDayOpenTime, 'EEE, MMM d')} at ${formatTimeDisplay(nextBusinessDayOpenTime)}`;
  }
  const hasNextMarketOpenLabel = nextMarketOpenLabel != null;

  return {
    hasNextMarketOpenLabel,
    nextMarketOpenLabel,
  };
};
